import { inject, Injectable } from '@angular/core';
import { CoaterLicense } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { PermissionService, User } from '@workspace-app/shared';

/**
 * Service to check user authorization for license operations
 * Based on Issue #661 - License Operations Authorization
 *
 * Authorization Matrix:
 * - QCT-Admin: Full access (Global scope) - all licenses, all operations
 * - QCT-User: Read access (Global scope) - all licenses, read/share only
 * - GL: Read access (Organisation scope) - own org licenses, read/share only
 * - Inspector: No license access
 */
@Injectable({
    providedIn: 'root'
})
export class LicenseAuthorizationService {
    private permissionService = inject(PermissionService);

    // Permission constants (match backend)
    private readonly PERMISSION_LICENSE_READ = 'License.Read';
    private readonly PERMISSION_LICENSE_WRITE = 'License.Write';
    private readonly PERMISSION_LICENSE_ADMIN = 'License.Admin';

    // Scope constants (match backend)
    private readonly SCOPE_GLOBAL = 'Global';
    private readonly SCOPE_ORGANISATION = 'Organisation';

    /**
     * Check if user can view license details
     * Requires: License.Read permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canViewLicense(user: User | null, license: CoaterLicense | null): boolean {
        if (!user || !license || !this.isUserValid(user)) return false;

        // Must have License.Read permission
        if (!this.hasLicenseReadPermission(user)) return false;

        // Check scope-based access
        return this.canAccessLicense(user, license);
    }

    /**
     * Check if user can update license details
     * Requires: License.Write permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canUpdateLicense(user: User | null, license: CoaterLicense | null): boolean {
        if (!user || !license || !this.isUserValid(user)) return false;

        // Must have License.Write permission
        if (!this.hasLicenseWritePermission(user)) return false;

        // Check scope-based access
        return this.canAccessLicense(user, license);
    }

    /**
     * Check if user can create/manage license renewal workflows
     * Requires: License.Admin permission (QCT-Admin only)
     * Note: Renewal workflows are QCT-only features, no scope filtering
     */
    canManageRenewalWorkflows(user: User | null): boolean {
        if (!user || !this.isUserValid(user)) return false;
        return this.hasLicenseAdminPermission(user);
    }

    /**
     * Check if user can view/download certificate
     * Requires: License.Read permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canViewCertificate(user: User | null, license: CoaterLicense | null): boolean {
        return this.canViewLicense(user, license);
    }

    /**
     * Check if user can share certificate token
     * Requires: License.Read permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canShareCertificate(user: User | null, license: CoaterLicense | null): boolean {
        if (!user || !license || !this.isUserValid(user)) return false;

        // Must have License.Read permission
        if (!this.hasLicenseReadPermission(user)) return false;

        // Check scope-based access
        return this.canAccessLicense(user, license);
    }

    /**
     * Check if user can revoke certificate token
     * Requires: License.Write permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canRevokeCertificateToken(user: User | null, license: CoaterLicense | null): boolean {
        return this.canUpdateLicense(user, license);
    }

    /**
     * Check if user can withdraw license
     * Requires: License.Admin permission (QCT-Admin only)
     */
    canWithdrawLicense(user: User | null): boolean {
        if (!user || !this.isUserValid(user)) return false;
        return this.hasLicenseAdminPermission(user);
    }

    /**
     * Check if user can archive license
     * Requires: License.Admin permission
     * Scope: Global (QCT) or Organisation (GL)
     */
    canArchiveLicense(user: User | null, license: CoaterLicense | null): boolean {
        if (!user || !license || !this.isUserValid(user)) return false;

        // Must have License.Admin permission
        if (!this.hasLicenseAdminPermission(user)) return false;

        // Check scope-based access
        return this.canAccessLicense(user, license);
    }

    /**
     * Check if user has global scope (can see all licenses)
     * QCT-Admin and QCT-User have global scope
     */
    hasGlobalScope(user: User | null): boolean {
        if (!user || !this.isUserValid(user)) return false;
        return user.scopes?.includes(this.SCOPE_GLOBAL) ?? false;
    }

    /**
     * Check if user has organisation scope (can see only own org licenses)
     * GL role has organisation scope
     */
    hasOrganisationScope(user: User | null): boolean {
        if (!user || !this.isUserValid(user)) return false;
        return user.scopes?.includes(this.SCOPE_ORGANISATION) ?? false;
    }

    /**
     * Check if user can access a specific license based on scope
     * - Global scope: Can access any license
     * - Organisation scope: Can only access licenses from user's organization
     */
    private canAccessLicense(user: User, _license: CoaterLicense): boolean {
        // Global scope - can access any license
        if (this.hasGlobalScope(user)) {
            return true;
        }

        // Organisation scope - check if license belongs to user's organization
        if (this.hasOrganisationScope(user)) {
            // Note: Organization ID comparison would happen here if we had
            // the user's organization IDs available in the User object.
            // For now, backend will enforce this via OData filtering,
            // so we just check the scope exists.
            return true;
        }

        // No valid scope - deny access
        return false;
    }

    /**
     * Check if user has License.Read permission
     */
    private hasLicenseReadPermission(user: User): boolean {
        return this.permissionService.hasEffectivePermission(user, this.PERMISSION_LICENSE_READ);
    }

    /**
     * Check if user has License.Write permission
     */
    private hasLicenseWritePermission(user: User): boolean {
        return this.permissionService.hasEffectivePermission(user, this.PERMISSION_LICENSE_WRITE);
    }

    /**
     * Check if user has License.Admin permission
     */
    private hasLicenseAdminPermission(user: User): boolean {
        return this.permissionService.hasEffectivePermission(user, this.PERMISSION_LICENSE_ADMIN);
    }

    /**
     * Check if user object has all required properties for authorization checks
     * A valid user must have Azure B2C authentication data and permission/role/scope arrays
     */
    private isUserValid(user: User | null): boolean {
        if (!user) return false;

        // Check for required authentication data
        const hasAuthData = user.azureB2C?.objectId != null;

        // Check for required permission/role/scope arrays
        const hasPermissions = Array.isArray(user.permissions);
        const hasRoles = Array.isArray(user.roles);
        const hasScopes = Array.isArray(user.scopes);

        return hasAuthData && hasPermissions && hasRoles && hasScopes;
    }
}
